import {Editor} from '@tiptap/react';
import {ReactComponent as HtmlIcon} from '../../../assets/images/html_code.svg';
import {ReactComponent as FormatIcon} from '../../../assets/images/formatting.svg';
import {ReactComponent as LinkIcon} from '../../../assets/images/insert_link.svg';
import {ReactComponent as FormatBoldIcon} from '../../../assets/images/bold-icon.svg';
import {ReactComponent as FormatItalicIcon} from '../../../assets/images/italic-icon.svg';
import {ReactComponent as StrikethroughSIcon} from '../../../assets/images/strike-icon.svg';
import {ReactComponent as FormatListBulletedIcon} from '../../../assets/images/bulletList-icon.svg';
import {ReactComponent as FormatListNumberedIcon} from '../../../assets/images/numberList-icon.svg';
import {ReactComponent as FormatIndentDecreaseIcon} from '../../../assets/images/leftIndent-icon.svg';
import {ReactComponent as FormatIndentIncreaseIcon} from '../../../assets/images/rightIndent-icon.svg';
import {ReactComponent as NotesIcon} from '../../../assets/images/align-icon.svg';
import {ReactComponent as HorizontalRuleIcon} from '../../../assets/images/horizontalRule-icon.svg';
import {useEffect, useRef, useState} from 'react';
import ArrMenu, {ArrMenuHandle, CustomMenuItem} from '../arr-menu/ArrMenu';
import ArrModal from '../arr-modal/ArrModal';
import {t} from 'i18next';
import ArrTextField from '../arr-text-field/ArrTextField';
import {FormControlLabel, Typography} from '@mui/material';
import ArrCheckbox from '../arr-checkbox/ArrCheckbox';
import ArrTooltip from '../arr-tooltip/ArrTooltip';

export const formatMenuItems = [
  {
    id: 'normal_text',
    label: 'Normal Text',
    disabled: false,
  },
  {
    id: 'quote',
    label: 'Quote',
    disabled: false,
  },
  {
    id: 'code',
    label: 'Code',
    disabled: false,
  },
  {
    id: 'header_1',
    label: 'Header 1',
    disabled: false,
  },
  {
    id: 'header_2',
    label: 'Header 2',
    disabled: false,
  },
  {
    id: 'header_3',
    label: 'Header 3',
    disabled: false,
  },
  {
    id: 'header_4',
    label: 'Header 4',
    disabled: false,
  },
  {
    id: 'header_5',
    label: 'Header 5',
    disabled: false,
  },
];

export const alignMenuItems = [
  {
    id: 'align_left',
    label: 'Align text to the left',
    disabled: false,
  },
  {
    id: 'align_center',
    label: 'Center text',
    disabled: false,
  },
  {
    id: 'align_right',
    label: 'Align text to the right',
    disabled: false,
  },
  {
    id: 'align_justify',
    label: 'Justify text',
    disabled: false,
  },
];

export const linkMenuItems = [
  {
    id: 'insert_link',
    label: 'Insert link',
    disabled: false,
  },
  {
    id: 'unlink',
    label: 'Unlink',
    disabled: false,
  },
];

type Props = {
  editor: Editor | null;
  isHtmlMode: boolean;
  onToggleHtmlMode: (value: boolean) => void;
};

export function Toolbar({editor, isHtmlMode, onToggleHtmlMode}: Props) {
  const formattingMenuRef = useRef<ArrMenuHandle>(null);
  const alignMenuRef = useRef<ArrMenuHandle>(null);
  const linkMenuRef = useRef<ArrMenuHandle>(null);
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [linkUrl, setLinkUrl] = useState<string>('');
  const [linkText, setLinkText] = useState<string>('');
  const [openInNewTab, setOpenInNewTab] = useState<boolean>(false);
  const [, setUpdate] = useState<number>(0);

  if (!editor) return null;

  const forceUpdate = () => setUpdate((prev) => prev + 1);

  const handleFormatOpen = (e: React.MouseEvent<HTMLButtonElement>) =>
    formattingMenuRef.current?.open(e.currentTarget);
  const handleAlignOpen = (e: React.MouseEvent<HTMLButtonElement>) =>
    alignMenuRef.current?.open(e.currentTarget);
  const handleLinkOpen = (e: React.MouseEvent<HTMLButtonElement>) =>
    linkMenuRef.current?.open(e.currentTarget);
  const handleFormatClose = () => {};
  const handleAlignClose = () => {};
  const handleLinkClose = () => {};

  const setFormatting = (action: CustomMenuItem) => {
    editor.chain().focus().clearNodes().run();
    switch (action.id) {
      case 'normal_text':
        editor.chain().focus().setParagraph().run();
        break;
      case 'quote':
        editor.chain().focus().toggleBlockquote().run();
        break;
      case 'code':
        editor.chain().focus().toggleCodeBlock().run();
        break;
      case 'header_1':
        editor.chain().focus().toggleHeading({level: 1}).run();
        break;
      case 'header_2':
        editor.chain().focus().toggleHeading({level: 2}).run();
        break;
      case 'header_3':
        editor.chain().focus().toggleHeading({level: 3}).run();
        break;
      case 'header_4':
        editor.chain().focus().toggleHeading({level: 4}).run();
        break;
      case 'header_5':
        editor.chain().focus().toggleHeading({level: 5}).run();
        break;
    }
    forceUpdate();
    handleFormatClose();
  };

  const setAligning = (action: CustomMenuItem) => {
    switch (action.id) {
      case 'align_left':
        editor.chain().focus().setTextAlign('left').run();
        break;
      case 'align_center':
        editor.chain().focus().setTextAlign('center').run();
        break;
      case 'align_right':
        editor.chain().focus().setTextAlign('right').run();
        break;
      case 'align_justify':
        editor.chain().focus().setTextAlign('justify').run();
        break;
    }
    forceUpdate();
    handleAlignClose();
  };

  const setLinking = (action: CustomMenuItem) => {
    if (action.id === 'insert_link') setDialogOpen(true);
    if (action.id === 'unlink') editor.chain().focus().unsetLink().run();
    forceUpdate();
    handleLinkClose();
  };

  const toggleMark = (mark: 'bold' | 'italic' | 'strike' | 'code') => {
    const toggleMap = {
      bold: () => editor.chain().focus().toggleBold().run(),
      italic: () => editor.chain().focus().toggleItalic().run(),
      strike: () => editor.chain().focus().toggleStrike().run(),
      code: () => editor.chain().focus().toggleCode().run(),
    } as const;

    type ToggleKey = keyof typeof toggleMap;

    toggleMap[mark as ToggleKey]?.();
    forceUpdate();
  };

  const insertLink = () => {
    if (!editor || !linkUrl.trim()) return;
    const {from, to} = editor.state.selection;
    const selectedText = editor.state.doc.textBetween(from, to, ' ');
    const html = `<a href="${linkUrl}"${openInNewTab ? ' target="_blank" rel="noopener noreferrer"' : ''}>${selectedText || linkText || linkUrl}</a>`;
    editor.chain().focus().insertContent(html).run();
    setDialogOpen(false);
    setLinkUrl('');
    setLinkText('');
    setOpenInNewTab(false);
    forceUpdate();
  };

  useEffect(() => {
    if (!editor || !dialogOpen) return;
    const {from, to} = editor.state.selection;
    setLinkText(editor.state.doc.textBetween(from, to, ' '));
    setLinkUrl(
      editor.isActive('link') ? editor.getAttributes('link').href || '' : ''
    );
    setOpenInNewTab(
      editor.isActive('link') &&
        editor.getAttributes('link').target === '_blank'
    );
  }, [dialogOpen]);

  return (
    <div className="toolbar" data-testid={`toolbar-link-url-link-text`}>
      {/* HTML */}

      <ArrTooltip
        content={t('richTextEditor.html')}
        offset={5}
        labelId={`tooltip-editor`}
      >
        <button
          onClick={() => onToggleHtmlMode(!isHtmlMode)}
          className={isHtmlMode ? 'active' : ''}
          aria-label={t('richTextEditor.html')}
          data-testid="toolbar-html"
          tabIndex={0}
        >
          <HtmlIcon />
        </button>
      </ArrTooltip>
      {/* Format */}
      <ArrTooltip
        content={t('richTextEditor.format')}
        offset={5}
        labelId={`tooltip-format`}
        tabIndex={0}
      >
        <button
          onClick={handleFormatOpen}
          disabled={isHtmlMode}
          aria-label={t('richTextEditor.format')}
          data-testid="toolbar-format"
        >
          <FormatIcon />
        </button>
      </ArrTooltip>
      <ArrMenu
        ref={formattingMenuRef}
        items={formatMenuItems}
        direction="bottom-left"
        subMenuDirection="right"
        onItemClick={setFormatting}
        className="text-editor-menu"
        onClose={handleFormatClose}
        id="format-submenu"
      />
      {/* Bold */}
      <ArrTooltip
        content={t('richTextEditor.bold')}
        offset={5}
        labelId={`tooltip-bold`}
        tabIndex={0}
      >
        <button
          onClick={() => toggleMark('bold')}
          disabled={
            isHtmlMode || !editor.can().chain().focus().toggleBold().run()
          }
          className={editor.isActive('bold') ? 'active' : ''}
          aria-label={t('richTextEditor.bold')}
          data-testid="toolbar-bold"
        >
          <FormatBoldIcon />
        </button>
      </ArrTooltip>
      {/* Italic */}
      <ArrTooltip
        content={t('richTextEditor.italic')}
        offset={5}
        labelId={`tooltip-italic`}
        tabIndex={0}
      >
        <button
          onClick={() => toggleMark('italic')}
          disabled={
            isHtmlMode || !editor.can().chain().focus().toggleItalic().run()
          }
          className={editor.isActive('italic') ? 'active' : ''}
          aria-label={t('richTextEditor.italic')}
          data-testid="toolbar-italic"
        >
          <FormatItalicIcon />
        </button>
      </ArrTooltip>
      {/* Strike */}
      <ArrTooltip content={t('richTextEditor.strikethrough')} offset={5} tabIndex={0}>
        <button
          onClick={() => toggleMark('strike')}
          disabled={
            isHtmlMode || !editor.can().chain().focus().toggleStrike().run()
          }
          className={editor.isActive('strike') ? 'active' : ''}
          aria-label={t('richTextEditor.strikethrough')}
          data-testid="toolbar-strikethrough"
        >
          <StrikethroughSIcon />
        </button>
      </ArrTooltip>
      {/* Bullet List */}
      <ArrTooltip content={t('richTextEditor.unorderedList')} offset={5}>
        <button
          onClick={() => {
            editor.chain().focus().toggleBulletList().run();
            forceUpdate();
          }}
          disabled={isHtmlMode}
          className={editor.isActive('bulletList') ? 'active' : ''}
          aria-label={t('richTextEditor.unorderedList')}
          data-testid="toolbar-unordered-list"
        >
          <FormatListBulletedIcon />
        </button>
      </ArrTooltip>
      {/* Ordered List */}
      <ArrTooltip content={t('richTextEditor.orderedList')} offset={5}>
        <button
          onClick={() => {
            editor.chain().focus().toggleOrderedList().run();
            forceUpdate();
          }}
          disabled={isHtmlMode}
          className={editor.isActive('orderedList') ? 'active' : ''}
          aria-label={t('richTextEditor.orderedList')}
          data-testid="toolbar-ordered-list"
        >
          <FormatListNumberedIcon />
        </button>
      </ArrTooltip>
      {/* Left Indent */}
      <ArrTooltip content={t('richTextEditor.outdent')} offset={5}>
        <button
          onClick={() => {
            editor.chain().focus().decreaseIndent().run();
            forceUpdate();
          }}
          disabled={isHtmlMode}
          aria-label={t('richTextEditor.outdent')}
          data-testid="toolbar-outdent"
        >
          <FormatIndentDecreaseIcon />
        </button>
      </ArrTooltip>
      {/* Right Indent */}
      <ArrTooltip content={t('richTextEditor.indent')} offset={5}>
        <button
          onClick={() => {
            editor.chain().focus().increaseIndent().run();
            forceUpdate();
          }}
          disabled={isHtmlMode}
          aria-label={t('richTextEditor.indent')}
          data-testid="toolbar-indent"
        >
          <FormatIndentIncreaseIcon />
        </button>
      </ArrTooltip>
      {/* Link */}
      <ArrTooltip content={t('richTextEditor.link')} offset={5}>
        <button
          onClick={handleLinkOpen}
          disabled={isHtmlMode}
          aria-label={t('richTextEditor.link')}
          data-testid="toolbar-link"
        >
          <LinkIcon />
        </button>
      </ArrTooltip>
      <ArrMenu
        ref={linkMenuRef}
        items={linkMenuItems}
        direction="bottom-left"
        subMenuDirection="right"
        onItemClick={setLinking}
        className="text-editor-link-menu"
        onClose={handleLinkClose}
        id="link-submenu"
      />
      {/* Alignment */}
      <ArrTooltip content={t('richTextEditor.alignment')} offset={5}>
        <button
          onClick={handleAlignOpen}
          disabled={isHtmlMode}
          aria-label={t('richTextEditor.alignment')}
          data-testid="toolbar-alignment"
        >
          <NotesIcon />
        </button>
      </ArrTooltip>
      <ArrMenu
        ref={alignMenuRef}
        items={alignMenuItems}
        direction="bottom-left"
        subMenuDirection="right"
        onItemClick={setAligning}
        className="text-editor-align-menu"
        onClose={handleAlignClose}
        id="align-submenu"
      />
      {/* Horizontal Rule */}
      <ArrTooltip content={t('richTextEditor.horizontalRule')} offset={5}>
        <button
          onClick={() => {
            editor.chain().focus().setHorizontalRule().run();
            forceUpdate();
          }}
          disabled={isHtmlMode}
          aria-label={t('richTextEditor.horizontalRule')}
          data-testid="toolbar-horizontal-rule"
        >
          <HorizontalRuleIcon />
        </button>
      </ArrTooltip>
      {/* Link Modal */}
      <ArrModal
        showModal={dialogOpen}
        title={t('richTextEditor.insertLink')}
        onClose={() => setDialogOpen(false)}
        autoFocusCloseButton={true}
        dataTestId="toolbar-modal"
        buttons={[
          {
            id: 'cancel-button',
            label: t('common.cancel'),
            dataTestId: 't-cancel-button',
            onClick: () => setDialogOpen(false),
            variant: 'secondary',
          },
          {
            id: 'insert-button',
            label: t('common.insert'),
            dataTestId: 't-insert-button',
            onClick: insertLink,
            variant: 'primary',
            disabled: linkUrl.trim() === '',
          },
        ]}
      >
        <div
          className="link-insert-modal-content"
          data-testid={`toolbar-link-url-link-text-btn`}
        >
          <div data-testid={`toolbar-link-url`}>
            <Typography
              className="typo-text body2_size input-text-editor-label"
              data-testid="input-url"
            >
              {t('richTextEditor.linkUrl')}
            </Typography>
            <ArrTextField
              className="option-number"
              state={'enabled'}
              sizeVariant="medium"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              showErrorIcon
              dataTestId="toolbar-url-to-assign"
            />
          </div>
          <div data-testid={`toolbar-link-text`}>
            <Typography
              className="typo-text body2_size input-text-editor-label"
              data-testid="input-link-text"
            >
              {t('richTextEditor.linkText')}
            </Typography>
            <ArrTextField
              className="option-number"
              state={'enabled'}
              sizeVariant="medium"
              value={linkText}
              onChange={(e) => setLinkText(e.target.value)}
              showErrorIcon
              dataTestId="toolbar-link-text-to-assign"
            />
          </div>
          <div data-testid={`toolbar-div`}>
            <FormControlLabel
              control={
                <ArrCheckbox
                  checked={openInNewTab}
                  onChange={() => setOpenInNewTab(!openInNewTab)}
                  data-testid={`open-in-new-tab-checkbox`}
                  size="small"
                  aria-label={t('richTextEditor.openInNewTab')}
                />
              }
              label={t('richTextEditor.openInNewTab')}
              className="typo-text body2_size input-text-editor-label"
              data-testid={`open-in-new-tab-label`}
            />
          </div>
        </div>
      </ArrModal>
    </div>
  );
}
